import { useEffect, useState } from "react";
import { APPLICATIONS } from "../lib/content";

// Logarithmic Drive — visualization of the three-component motion.
//
// Both the INPUT track (red ellipse) and the OUTPUT track (zig-zag) are
// drawn so they intersect at every bearing-ball centre. The balls sit in
// the modulator's radial slots and ride on BOTH tracks simultaneously.
//
// Mathematical fact: a ball at angular position θ_k has radial position
//     r(θ_k, t) = BASE_R + AMP · cos(2 · (θ_k − φ_in(t)))
// which is exactly the polar equation of an ellipse rotating with the
// input. The output track is constructed dynamically as a chevron whose
// peaks coincide with every ball centre — so balls always lie on it too.

export default function LogDriveDiagram({ className = "" }) {
  const [t, setT] = useState(0);

  useEffect(() => {
    let raf;
    const start = performance.now();
    const tick = (now) => {
      setT((now - start) / 1000);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const cx = 200;
  const cy = 200;

  const N_BALLS = 12;
  const N_TEETH = 14;          // wave-drive constraint: N_lobes + N_balls
  const BALL_R = 9;
  const BASE_R = 110;
  const AMP = 16;

  // Modulator slot extents
  const SLOT_INNER = BASE_R - AMP - BALL_R - 5;
  const SLOT_OUTER = BASE_R + AMP + BALL_R + 5;

  // INPUT track (rigid red oval)
  const INPUT_A = BASE_R + AMP;
  const INPUT_B = BASE_R - AMP;

  // OUTPUT track (rigid scalloped ring)
  const OUT_AVG = BASE_R + BALL_R;
  const OUT_AMP = AMP;
  const OUT_RIM = OUT_AVG + AMP + 16;
  const HOUSING_R = OUT_RIM + 6;

  // Animation timing — output rotates 7× slower than input (same direction)
  const INPUT_PERIOD = 6.5;
  const inputAngle = (t * 2 * Math.PI) / INPUT_PERIOD;
  const outputAngle = inputAngle / 7;

  // INPUT track — cosine cam profile r(α) = BASE_R + AMP·cos(2α). Not a
  // true ellipse, but mathematically passes through every ball centre.
  const SAMPLES_IN = 240;
  const inPts = [];
  for (let i = 0; i <= SAMPLES_IN; i++) {
    const a = (i * 2 * Math.PI) / SAMPLES_IN;
    const r = BASE_R + AMP * Math.cos(2 * a);
    inPts.push(`${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`);
  }
  const inputPath = `M ${inPts.join(" L ")} Z`;

  // Bearing balls — radial position from the rotating input cam (exact)
  const balls = [];
  for (let i = 0; i < N_BALLS; i++) {
    const theta = (i * 2 * Math.PI) / N_BALLS;
    const r = BASE_R + AMP * Math.cos(2 * (theta - inputAngle));
    balls.push({
      x: cx + r * Math.cos(theta),
      y: cy + r * Math.sin(theta),
      theta,
      r,
    });
  }

  // OUTPUT TRACK — rigid, smooth scalloped ring (no sharp points).
  // Sampled densely so the inner profile is rendered as smooth arcs.
  const SAMPLES = N_TEETH * 12;
  const innerPts = [];
  for (let i = 0; i <= SAMPLES; i++) {
    const a = (i * 2 * Math.PI) / SAMPLES;
    const r = OUT_AVG + OUT_AMP * Math.cos(N_TEETH * a);
    innerPts.push(`${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`);
  }
  const trackPath = `M ${innerPts.join(" L ")} Z`;

  return (
    <div
      className={`relative aspect-square w-full ${className}`}
      data-testid="logdrive-diagram"
    >
      <svg
        viewBox="0 0 400 400"
        className="absolute inset-0 w-full h-full"
        role="img"
        aria-label="Logarithmic Drive — three-component motion diagram"
      >
        <defs>
          <radialGradient id="ballGrad" cx="35%" cy="35%" r="65%">
            <stop offset="0%" stopColor="#ffffff" />
            <stop offset="60%" stopColor="#cfcfcf" />
            <stop offset="100%" stopColor="#5b5b5b" />
          </radialGradient>
          <radialGradient id="metal" cx="50%" cy="50%" r="55%">
            <stop offset="0%" stopColor="#1c1c1c" />
            <stop offset="100%" stopColor="#080808" />
          </radialGradient>
        </defs>

        {/* OUTPUT — rigid disc, rotates slowly (1/7 input speed) */}
        <g
          transform={`rotate(${(outputAngle * 180) / Math.PI} ${cx} ${cy})`}
        >
          <circle
            cx={cx}
            cy={cy}
            r={HOUSING_R + 8}
            fill="none"
            stroke="rgba(245,158,11,0.55)"
            strokeWidth="4"
            strokeDasharray="7 9"
          />
          <circle
            cx={cx}
            cy={cy}
            r={OUT_RIM}
            fill="url(#metal)"
            stroke="rgba(245,158,11,0.35)"
          />
          {/* Output track — rigid scalloped (cosine arc) inner profile */}
          <path
            d={trackPath}
            fill="rgba(245,158,11,0.05)"
            stroke="#F59E0B"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
          {/* tick to convey output rotation — riding on the external dashed circle */}
          <rect
            x={cx + HOUSING_R + 8 - 6}
            y={cy - 3}
            width={12}
            height={6}
            fill="#F59E0B"
          />
        </g>

        {/* MODULATOR — stationary radial slots (no rotation) */}
        <g>
          {Array.from({ length: N_BALLS }).map((_, i) => {
            const deg = (i * 360) / N_BALLS;
            return (
              <g
                key={`slot-${i}`}
                transform={`rotate(${deg} ${cx} ${cy})`}
              >
                <rect
                  x={cx + SLOT_INNER}
                  y={cy - (BALL_R + 1)}
                  width={SLOT_OUTER - SLOT_INNER}
                  height={(BALL_R + 1) * 2}
                  rx={3}
                  fill="rgba(255,255,255,0.04)"
                  stroke="#FFFFFF"
                  strokeWidth="1"
                  strokeOpacity="0.85"
                />
              </g>
            );
          })}
          <circle
            cx={cx}
            cy={cy}
            r={SLOT_OUTER + 3}
            fill="none"
            stroke="rgba(255,255,255,0.25)"
          />
          <circle
            cx={cx}
            cy={cy}
            r={SLOT_INNER - 3}
            fill="none"
            stroke="rgba(255,255,255,0.25)"
          />
        </g>

        {/* BEARING BALLS — translate radially, ride both tracks at once */}
        {balls.map((b, i) => (
          <circle
            key={`ball-${i}`}
            cx={b.x}
            cy={b.y}
            r={BALL_R}
            fill="url(#ballGrad)"
            stroke="rgba(0,0,0,0.6)"
            strokeWidth="0.5"
          />
        ))}

        {/* INPUT TRACK — red oval, rotates fast, drawn ON TOP of zig-zag.
            Mathematically traverses every ball centre simultaneously. */}
        <g
          transform={`rotate(${(inputAngle * 180) / Math.PI} ${cx} ${cy})`}
        >
          <path
            d={inputPath}
            fill="rgba(255,59,48,0.06)"
            stroke="#FF3B30"
            strokeWidth="2"
            strokeLinejoin="round"
          />
          {/* Input shaft — open through-hole, rim sits just inside the modulator slots */}
          <circle
            cx={cx}
            cy={cy}
            r={SLOT_INNER - 8}
            fill="none"
            stroke="#FF3B30"
            strokeWidth="4"
          />
          {/* Keyway tick to convey input rotation */}
          <rect
            x={cx - 3}
            y={cy - (SLOT_INNER - 8)}
            width={6}
            height={14}
            fill="#FF3B30"
          />
        </g>

        {/* Callouts */}
        <g
          fontFamily="JetBrains Mono, monospace"
          fontSize="9"
          textAnchor="middle"
        >
          <text x="200" y="20" fill="#F59E0B">OUTPUT TRACK</text>
          <text x="200" y="386" fill="#FF3B30">INPUT TRACK</text>
        </g>
        <g
          fontFamily="JetBrains Mono, monospace"
          fontSize="9"
          fill="#FFFFFF"
          textAnchor="end"
        >
          <line
            x1="320"
            y1="64"
            x2="360"
            y2="40"
            stroke="rgba(255,255,255,0.45)"
          />
          <text x="395" y="36">MODULATOR</text>
          <text x="395" y="48" fillOpacity="0.75">radial slots</text>
        </g>
      </svg>
    </div>
  );
}

export function ApplicationMini() {
  return (
    <ul className="font-mono-tech text-[11px] uppercase tracking-widest text-white/60 space-y-2">
      {APPLICATIONS.map((a) => (
        <li key={a.slug} className="flex items-center gap-3">
          <span className="h-1 w-1 bg-[#FF3B30]" /> {a.title}
        </li>
      ))}
    </ul>
  );
}
